import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { BaseLayout } from '../../components/Layout/Layout';
import { useLanguage } from '../../locale/useLanguage';
import { Routes } from '../../routes/types';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './ChooseDeliveryTimeView.css';
import { Button } from '../../components/Button/Button';
import { DateTimePickerModel } from '../../models/chooseDeliveryDate.models';
import { DateSlider } from './components/DateSlider/DateSlider';
import dayjs, { Dayjs } from 'dayjs';
import { HourSlider, TimeSpan } from './components/HourSlider/HourSlider';
import { useGetCmsConfigsQuery } from '../../apis/cmsApi/cms.hooks.api';
import { PresetsSlider } from './components/PresetsSlider/PresetsSlider';
import { useBack } from '../../hooks/useBack';
import {
  TimePickerAction,
  TimePickerActionResult,
  TimePickerController,
} from './components/TimePickerController';
import {
  FaqFeature,
  OperationType,
  Product,
  TimePickerPreset,
} from '../../models';
import { dateFormatRange, dateHoursFormatRange } from '../../helpers/dateTimes';
import {
  useAddCartMutation,
  useEditCartItemMutation,
  useGetOrderQuery,
  useGetServiceOptionsQuery,
  usePurchaseFreeItemMutation,
  usePurchaseNotLoggedFreeItemMutation,
} from '../../apis/mlkApi/mlk.api.hooks';
import { useOrderId } from '../../hooks/useOrderId';
import { parseRes } from '../../apis/utils';
import {
  CartElement,
  RescheduleCartElement,
} from '../../models/payment.models';
import { StrongRegistrationWrap } from '../../components/StrongRegistrationWrap/StrongRegistrationWrap';
import { useDispatch, useSelector } from 'react-redux';
import { setOperation } from '../../redux/general/general.actions';
import { getOperationSelector } from '../../redux/general/general.selectors';
import { ConfirmDeliveryTimeModal } from './components/ConfirmDeliveryTime/ConfirmDeliveryTime';
import { ModalRef } from '../../components/Modal/Modal';
import { NoAvailabilityModal } from './components/NoAvailabilityModal/NoAvailabilityModal';
import { getCorrectStatus } from '../../helpers/order';
import { GaEvent, pushEvent } from '../../helpers/googleAnalytics';
import { authTokenSelector } from '../../redux/user/user.selectors';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { SerializedError } from '@reduxjs/toolkit';
import { useIsNoAssign } from '../../hooks/useIsNoAssign';

type ChooseDeliveryTimeStateParams =
  | {
      cartItemInEdit: RescheduleCartElement;
    }
  | undefined;

type ChooseDeliveryTimeViewProps = {};

export const ChooseDeliveryTimeView: React.FC<
  ChooseDeliveryTimeViewProps
> = () => {
  const [t] = useLanguage();
  const history = useHistory();
  const location = useLocation<ChooseDeliveryTimeStateParams>();
  const back = useBack();
  const dispatch = useDispatch();
  const orderId = useOrderId();
  const operation = useSelector(getOperationSelector);
  const authToken = useSelector(authTokenSelector);
  const isNoAssign = useIsNoAssign();

  const { data: order } = useGetOrderQuery(
    { orderId: orderId || '', isNoAssign },
    { skip: !orderId }
  );
  const { data: serviceOptions } = useGetServiceOptionsQuery(orderId || '', {
    skip: !orderId,
  });
  const { isLoading, data: cmsConfig } = useGetCmsConfigsQuery(undefined);
  const [addToCartApiCall] = useAddCartMutation();
  const [purchaseFreeAppointmentApiCall] = usePurchaseFreeItemMutation();
  const [purchaseNotLoggedFreeAppointmentApiCall] =
    usePurchaseNotLoggedFreeItemMutation();
  const [editCartItemApiCall] = useEditCartItemMutation();

  const [dateTimePickerModel, setDateTimePickerModel] =
    useState<DateTimePickerModel>();

  const timePickerController = useRef<TimePickerController>();
  const [startInitialIndex, setStartInitialIndex] = useState<[number]>();
  const [endInitialIndex, setEndInitialIndex] = useState<[number]>();
  const [price, setPrice] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(0);
  const [priceClass, setPriceClass] = useState('edit-delivery-time--pulse');
  const [selectedDate, setSelectedDate] = useState<Dayjs>();
  const [startSpans, setStartSpans] = useState<TimeSpan[]>();
  const [endSpans, setEndSpans] = useState<TimeSpan[]>();
  const [presets, setPresets] = useState<TimePickerPreset[]>();
  const [presetIndex, setPresetIndex] = useState<number>();
  const [timeRange, setTimeRange] = useState<string>();
  const [strgRange, setStrgRange] = useState<string>();
  const modalRef = useRef<ModalRef>(null);
  const noAvailabilityModalRef = useRef<ModalRef>(null);

  useEffect(() => {
    if (
      cmsConfig &&
      order &&
      serviceOptions?.defaultRanges &&
      serviceOptions?.defaultRanges.length > 0
    ) {
      setDateTimePickerModel(
        new DateTimePickerModel(serviceOptions, cmsConfig.onlyFreeAppointments)
      );
    } else if (
      serviceOptions &&
      (!serviceOptions?.defaultRanges ||
        serviceOptions.defaultRanges.length === 0)
    ) {
      noAvailabilityModalRef.current?.openModal();
    }
  }, [cmsConfig, history, order, serviceOptions]);

  const goBack = useCallback(() => {
    dispatch(setOperation(null));
    back();
  }, [back, dispatch]);

  const updateParams = useCallback(
    (actionResult: TimePickerActionResult) => {
      setSelectedDate(actionResult.date);
      setOriginalPrice(actionResult.originalPrice);
      setPrice(actionResult.price);
      setStartSpans(actionResult.startTimes);
      setEndSpans(actionResult.endTimes);
      setPresets(actionResult.presets);
      setPresetIndex(actionResult.presetIndex);
      setStartInitialIndex([actionResult.startTimeIndex]);
      setEndInitialIndex([actionResult.endTimeIndex]);
      if (operation) {
        dispatch(
          setOperation({
            ...operation,
            operationType:
              actionResult.price === 0
                ? OperationType.FreeReschedule
                : OperationType.Reschedule,
            savedData: {
              startTime: actionResult.startTimes[actionResult.startTimeIndex],
              endTime: actionResult.endTimes[actionResult.endTimeIndex],
              date: actionResult.date.format('YYYY-MM-DD'),
            },
          })
        );
      }
    },
    [dispatch, operation]
  );

  useEffect(() => {
    if (dateTimePickerModel && cmsConfig && !timePickerController.current) {
      timePickerController.current = new TimePickerController(
        dateTimePickerModel,
        cmsConfig,
        operation?.savedData
      );
      const timePickerParams =
        timePickerController.current.getActionReturnValue();
      updateParams(timePickerParams);
    }
  }, [cmsConfig, dateTimePickerModel, operation?.savedData, updateParams]);

  useEffect(() => {
    setPriceClass('edit-delivery-time--pulse');
    const timeout = setTimeout(() => {
      setPriceClass('');
    }, 300);

    return () => clearTimeout(timeout);
  }, [price]);

  const sendFreeRescheduleEvent = useCallback(() => {
    const start = strgRange?.split('/')[0];
    const end = strgRange?.split('/')[1];
    if (order) {
      const calculatedOrderStatus = getCorrectStatus(order);
      pushEvent(GaEvent.FreeReschedule, {
        name: GaEvent.FreeReschedule,
        shipper_order_code: order.shipperOrderCode,
        milkman_order_state: order.status,
        order_id: order.id,
        shipper_id: order.shipper.id,
        shipper_name: order.shipper.name,
        order_state_calculated: calculatedOrderStatus!,
        territory_id: +order.shipperTerritoryConfiguration.territoryId,
        date: dayjs().format(dateFormatRange),
        start_range: dayjs(start).format(dateHoursFormatRange),
        end_range: dayjs(end).format(dateHoursFormatRange),
      });
    }
  }, [order, strgRange]);

  const thenFreeRescheduleFunction = useCallback(
    (
      res:
        | {
            data: CartElement;
          }
        | {
            error: FetchBaseQueryError | SerializedError;
          }
    ) => {
      const { successRes, errorRes } = parseRes(res);
      if (successRes.data) {
        sendFreeRescheduleEvent();
        dispatch(setOperation(null));
        history.replace(Routes.ConfirmationFree, {
          range: strgRange,
          address: order?.deliveryAddress.address,
        });
      }
      if (errorRes) {
        // TODO: Handle error
      }
    },
    [
      dispatch,
      history,
      order?.deliveryAddress.address,
      sendFreeRescheduleEvent,
      strgRange,
    ]
  );

  const purchaseNotLoggedFree = useCallback(() => {
    strgRange &&
      purchaseNotLoggedFreeAppointmentApiCall({
        body: { timeRange: strgRange },
        id: order!.id,
      }).then((res) => {
        thenFreeRescheduleFunction(res);
      });
  }, [
    order,
    purchaseNotLoggedFreeAppointmentApiCall,
    strgRange,
    thenFreeRescheduleFunction,
  ]);

  const purchaseFree = useCallback(() => {
    strgRange &&
      purchaseFreeAppointmentApiCall({
        product: Product.Appointment,
        orderId: order!.id,
        configuration: {
          timeRange: strgRange,
        },
      }).then((res) => {
        thenFreeRescheduleFunction(res);
      });
  }, [
    order,
    purchaseFreeAppointmentApiCall,
    strgRange,
    thenFreeRescheduleFunction,
  ]);
  const onDateChange = useCallback(
    (newDate: Dayjs) => {
      const actionResult = timePickerController.current!.exec(
        TimePickerAction.EditDate,
        { date: newDate }
      );
      updateParams(actionResult);
    },
    [updateParams]
  );

  const onPresetChange = useCallback(
    (presetIndex: number) => {
      const actionResult = timePickerController.current!.exec(
        TimePickerAction.EditPreset,
        { presetIndex }
      );
      updateParams(actionResult);
    },
    [updateParams]
  );

  const onEndTimeChange = useCallback(
    (endTimeIndex: number) => {
      const actionResult = timePickerController.current!.exec(
        TimePickerAction.EditEndTime,
        { endTimeIndex }
      );
      updateParams(actionResult);
    },
    [updateParams]
  );

  const onStartTimeChange = useCallback(
    (startTimeIndex: number) => {
      const actionResult = timePickerController.current!.exec(
        TimePickerAction.EditStartTime,
        { startTimeIndex }
      );
      updateParams(actionResult);
    },
    [updateParams]
  );

  const goToInfo = useCallback(() => {
    history.push(Routes.Faq.replace(':feature', FaqFeature.TimePicker));
  }, [history]);

  const addToCart = useCallback(
    (range: string) => {
      addToCartApiCall({
        product: Product.Appointment,
        orderId: order!.id,
        configuration: {
          timeRange: range,
        },
      }).then((res) => {
        const { successRes, errorRes } = parseRes(res);
        if (successRes) {
          dispatch(setOperation(null));
          history.push(Routes.Cart);
        }
        if (errorRes) {
          // TODO: Handle error
        }
      });
    },
    [addToCartApiCall, dispatch, history, order]
  );

  const purchaseFreeAppointment = useCallback(() => {
    if (strgRange)
      if (!authToken) {
        purchaseNotLoggedFree();
      } else {
        purchaseFree();
      }
  }, [strgRange, authToken, purchaseNotLoggedFree, purchaseFree]);

  const editCartItem = useCallback(
    (range: string) => {
      if (!location.state) return;
      editCartItemApiCall({
        cartElement: {
          id: location.state.cartItemInEdit.id,
          product: location.state.cartItemInEdit.product,
          orderId: location.state.cartItemInEdit.orderId,
          configuration: {
            timeRange: range,
          },
        },
      }).then((res) => {
        const { successRes, errorRes } = parseRes(res);
        if (successRes.data) {
          dispatch(setOperation(null));
          history.push(Routes.Cart);
        }
        if (errorRes) {
          // TODO: Handle error
        }
      });
    },
    [dispatch, editCartItemApiCall, history, location.state]
  );

  const continueToCart = useCallback(() => {
    const chosenRange = timePickerController.current?.getState();
    setTimeRange(`${chosenRange?.startTime.time}/${chosenRange?.endTime.time}`);
    const stringDate = chosenRange?.date.format('YYYY-MM-DD');

    const referenceYear = chosenRange?.date.year();
    // find the last sunday of march and the last sunday of october in the reference year
    const lastDayOfMarch = dayjs(`${referenceYear}-03-31`);
    const lastDayOfOctober = dayjs(`${referenceYear}-10-31`);
    // Find the day of the week of the last day of March
    const daysToSubtractMarch = lastDayOfMarch.day();
    const daysToSubtractOctober = lastDayOfOctober.day();
    // Subtract the number of days to get to the last Sunday
    const daylightSavingTimeStart = lastDayOfMarch
      .subtract(daysToSubtractMarch, 'day')
      .hour(0);
    const daylightSavingTimeEnd = lastDayOfOctober
      .subtract(daysToSubtractOctober, 'day')
      .hour(0);

    let stringRange: string;
    if (
      chosenRange?.date.isBefore(daylightSavingTimeStart) ||
      chosenRange?.date.isSameOrAfter(daylightSavingTimeEnd)
    ) {
      stringRange = `${stringDate}T${chosenRange?.startTime.time}:00.000+01:00/${stringDate}T${chosenRange?.endTime.time}:00.000+01:00`;
    } else {
      stringRange = `${stringDate}T${chosenRange?.startTime.time}:00.000+02:00/${stringDate}T${chosenRange?.endTime.time}:00.000+02:00`;
    }
    setStrgRange(stringRange);
    if (chosenRange) {
      if (chosenRange!.price === 0) {
        modalRef.current?.openModal();
      } else if (location.state) {
        editCartItem(stringRange);
      } else {
        addToCart(stringRange);
      }
    }
  }, [addToCart, editCartItem, location.state]);

  const goBackToHome = useCallback(() => {
    noAvailabilityModalRef.current?.closeModal();
    history.replace(Routes.Home);
  }, [history]);

  return (
    <>
      <BaseLayout
        title={t('pageTitles.whenCome')}
        hasInfo
        hasBack
        onInfoClick={goToInfo}
        onBackClick={goBack}
      >
        {!isLoading && (
          <div className="edit-delivery-time--container">
            <section className="edit-delivery-time--default-slots-container">
              <div className="edit-delivery-time--section-title-container">
                <h2 className="no-highlight edit-delivery-time--section-title">
                  {t('chooseDeliveryTime.chooseTimeFrame')}
                </h2>
              </div>
              {presets && presetIndex !== undefined && (
                <PresetsSlider
                  onPresetChange={onPresetChange}
                  selectedIndex={presetIndex!}
                  presets={presets}
                />
              )}
            </section>
            <section className="edit-delivery-time--date-section-container">
              <div className="edit-delivery-time--section-title-container">
                <h2 className="no-highlight edit-delivery-time--section-title">
                  {t('chooseDeliveryTime.deliveryDate')}
                </h2>
              </div>
              {selectedDate && dateTimePickerModel?.availableDates && (
                <DateSlider
                  date={selectedDate.format(dateFormatRange)}
                  dates={dateTimePickerModel?.availableDates!}
                  onDateChange={onDateChange}
                />
              )}
            </section>
            <section className="edit-delivery-time--hour-section-container">
              <div>
                <div className="edit-delivery-time--section-title-container">
                  <h2 className="no-highlight edit-delivery-time--section-title">
                    {t('chooseDeliveryTime.fromHour')}
                  </h2>
                </div>
                <p className="no-highlight edit-delivery-time--section-tip">
                  {t('chooseDeliveryTime.fromHourTip')}
                </p>
              </div>
              {startInitialIndex !== undefined && startSpans && (
                <HourSlider
                  spans={startSpans}
                  afterChange={onStartTimeChange}
                  initialIndex={startInitialIndex}
                />
              )}
            </section>
            <section className="edit-delivery-time--hour-section-container">
              <div>
                <div className="edit-delivery-time--section-title-container">
                  <h2 className="no-highlight edit-delivery-time--section-title">
                    {t('chooseDeliveryTime.toHour')}
                  </h2>
                </div>
                <p className="no-highlight edit-delivery-time--section-tip">
                  {t('chooseDeliveryTime.toHourTip')}
                </p>
              </div>
              {endInitialIndex !== undefined && endSpans && (
                <HourSlider
                  spans={endSpans}
                  initialIndex={endInitialIndex}
                  afterChange={onEndTimeChange}
                />
              )}
            </section>
            <section className="edit-delivery-time--action-section-container">
              <div className="edit-delivery-time--price-container">
                <div className="edit-delivery-time--line" />
                <div>
                  <p
                    className={`no-highlight edit-delivery-time--price-paragraph ${priceClass}`}
                  >
                    {t('chooseDeliveryTime.total')}{' '}
                    <span className={`edit-delivery-time--price`}>
                      € {price.toFixed(2).replace('.', ',')}
                    </span>
                  </p>
                  {originalPrice !== price && (
                    <div className="edit-delivery-time--sale-container ">
                      <small className="edit-delivery-time--sale-price">
                        € {originalPrice.toFixed(2).replace('.', ',')}
                      </small>
                    </div>
                  )}
                </div>
                <div className="edit-delivery-time--line" />
              </div>
              <div className="edit-delivery-time--button-container">
                <StrongRegistrationWrap
                  useDispatchOperationNull={false}
                  operationType={
                    operation &&
                    (operation.operationType === OperationType.FreeReschedule ||
                      operation.operationType === OperationType.Reschedule)
                      ? operation.operationType
                      : OperationType.FreeReschedule
                  }
                  successAction={continueToCart}
                >
                  <Button label={t('chooseDeliveryTime.continue')} isPrimary />
                </StrongRegistrationWrap>
              </div>
              <div className="edit-delivery-time--button-container">
                <Button onClick={back} label={t('chooseDeliveryTime.cancel')} />
              </div>
            </section>
            <ConfirmDeliveryTimeModal
              ref={modalRef}
              range={timeRange}
              date={dayjs(selectedDate).format('dddd, D MMMM YYYY')}
              onConfirm={purchaseFreeAppointment}
            />
          </div>
        )}
      </BaseLayout>
      <NoAvailabilityModal
        ref={noAvailabilityModalRef}
        onBackToHome={goBackToHome}
      />
    </>
  );
};
